import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Star, Truck, ShoppingCart, X, MapPin, Search } from "lucide-react";
import FooterDisplay from "../components/footer";
import WholesaleImg from "../assets/images/new_ui/wholesale.jpg";
import RestaurantMk from "../assets/images/new_ui/restaurant_mk.png";
import PlaceholderImg from "../assets/images/new_ui/placeholder.jpg";
import { useNavigate } from "react-router-dom";
import StoreMkImg from "../assets/images/new_ui/grocerie_mk.png";
import {
  _get_discovery_trending,
  _get_all_state,
  _get_cities_by_state_code,
} from "../common/axios_services";
import { showErrorNotification } from "../common/utils/notification";
import LocationModal from "../components/LocationModal";
import BusinessCard from "../components/BusinessCard";
import HelpSupportButton from "../components/HelpSupportButton";
import { useSelectedLocation } from "../hooks/useSelectedLocation";

export default function KonnectMarketplace() {
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.userProfile.user);
  const { saveLocation, getLocation } = useSelectedLocation();

  const [cartCount, setCartCount] = useState(0);
  const [showCart, setShowCart] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isAutoSliding, setIsAutoSliding] = useState(true);

  const [marketplaceData, setMarketplaceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [allStates, setAllStates] = useState([]);
  const [allLgas, setAllLgas] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedLga, setSelectedLga] = useState(null);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setSelectedLga(null);
    fetchLgas(state.name);
  };

  const handleLgaSelect = (lga) => {
    setSelectedLga(lga);
    const location = {
      name: lga.name,
      state: selectedState.name,
    };
    setSelectedLocation(location);
    saveLocation(selectedState, lga);
  };

  const handleContinue = async () => {
    if (selectedLocation) {
      setShowLocationModal(false);
      await fetchMarketplaceData();
    }
  };

  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const response = await _get_all_state();
      setAllStates(response.data.data);
    } catch (err) {
      showErrorNotification("Failed to load states");
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchLgas = async (stateName) => {
    setLoadingLgas(true);
    try {
      const response = await _get_cities_by_state_code(stateName);
      setAllLgas(response.data.data);
    } catch (err) {
      showErrorNotification("Failed to load Locations");
    } finally {
      setLoadingLgas(false);
    }
  };

  const fetchMarketplaceData = async () => {
    if (!selectedLocation) return;

    setLoading(true);
    setError(null);

    try {
      const state = selectedLocation.state;
      const lga = selectedLocation.name;

      const response = await _get_discovery_trending({ state, lga });
      setMarketplaceData(response.data?.data);
    } catch (err) {
      setError(err.message || "Failed to load marketplace data");
      showErrorNotification(err.message || "Failed to load marketplace data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketplaceDataForLocation = async (location) => {
    setLoading(true);
    setError(null);

    try {
      let params = {};
      if (location) {
        const state = location.state;
        const lga = location.name;
        params = { state, lga };
      } else {
        params = { state: "", lga: "" };
      }

      const response = await _get_discovery_trending(params);
      setMarketplaceData(response.data?.data);
    } catch (err) {
      setError(err.message || "Failed to load marketplace data");
      showErrorNotification(err.message || "Failed to load marketplace data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeLocation = async () => {
      const savedLocation = getLocation();
      if (savedLocation) {
        setSelectedLocation({
          state: savedLocation.state,
          name: savedLocation.lga,
        });
        await fetchMarketplaceDataForLocation({
          state: savedLocation.state,
          name: savedLocation.lga,
        });
      } else if (userProfile?.state && userProfile?.lga) {
        const location = {
          name: userProfile.lga,
          state: userProfile.state,
        };
        setSelectedLocation(location);
        saveLocation(userProfile.state, userProfile.lga);
        await fetchMarketplaceDataForLocation(location);
      } else {
        setShowLocationModal(true);
        await fetchMarketplaceDataForLocation(null);
      }
    };

    initializeLocation();
  }, [userProfile]);

  useEffect(() => {
    if (showLocationModal && allStates.length === 0) {
      fetchStates();
    }
  }, [showLocationModal]);

  const heroSlides = [
    {
      title: "Hot Naija food, delivered <br />FREE!",
      subtitle:
        "Order now, get it instantly or choose the day you want it. Office lunch, family pack, birthday, owambe - just name am!",
      image: RestaurantMk,
      handleClick: () => navigate("/restaurant"),
    },
    {
      title: "Buy wholesale items from our vendors at best prices",
      subtitle: "Buy at a lesser price",
      image: WholesaleImg,
      handleClick: () => navigate("/raw_food"),
    },
    {
      title: "Buy your groceries and we <br />deliver for free",
      subtitle: "Free and fast delivery!",
      image: StoreMkImg,
      handleClick: () => navigate("/store"),
    },
  ];

  useEffect(() => {
    if (!isAutoSliding) return;

    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoSliding, heroSlides.length]);

  const pauseAutoSlide = () => {
    setIsAutoSliding(false);
    setTimeout(() => setIsAutoSliding(true), 10000);
  };

  const addToCart = () => {
    setCartCount(cartCount + 1);
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    pauseAutoSlide();
  };

  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const EmptyState = ({ title, message }) => (
    <div className="text-center py-12">
      <div className="text-gray-400 mb-4">
        <ShoppingCart size={48} className="mx-auto" />
      </div>
      <h3 className="text-xl font-semibold text-gray-600 mb-2">{title}</h3>
      <p className="text-gray-500 mb-6">{message}</p>
      <button
        onClick={() => setShowLocationModal(true)}
        className="bg-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-600 transition"
      >
        Change Location
      </button>
    </div>
  );

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }
    if (isRightSwipe) {
      setCurrentSlide(
        (prev) => (prev - 1 + heroSlides.length) % heroSlides.length,
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <LocationModal
        showLocationModal={showLocationModal}
        setShowLocationModal={setShowLocationModal}
        allStates={allStates}
        allLgas={allLgas}
        selectedState={selectedState}
        selectedLga={selectedLga}
        handleStateSelect={handleStateSelect}
        handleLgaSelect={handleLgaSelect}
        selectedLocation={selectedLocation}
        handleContinue={handleContinue}
        loadingStates={loadingStates}
        loadingLgas={loadingLgas}
      />

      {/* Hero Section with Carousel */}
      <div
        className={`relative bg-gray-50 overflow-hidden transition-colors duration-500`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-2 bg-blue-100 backdrop-blur-s px-4 py-2 rounded-full hover:bg-blue-100 transition"
          >
            <MapPin size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-600">
              {selectedLocation
                ? `${selectedLocation.name}, ${
                    selectedLocation.state?.split(" ")[0] || ""
                  }`
                : "Select Location"}
            </span>
          </button>
        </div>

        <div
          className="flex pt-10 transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {heroSlides.map((slide, index) => (
            <div key={index} className="min-w-full">
              <div className="grid grid-cols-1 gap-8 items-center ">
                <div className="px-4 pt-12 sm:pt-16">
                  <h1
                    className="text-4xl sm:text-5xl font-bold mb-4 leading-tighter"
                    dangerouslySetInnerHTML={{ __html: slide.title }}
                  />
                  <button
                    onClick={slide.handleClick}
                    className="bg-gray-900 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
                  >
                    Start Here
                  </button>
                </div>
                <div className="flex justify-center">
                  <img
                    src={slide.image || PlaceholderImg}
                    alt={slide.title}
                    className="w-full h-[180px] object-cover object-center"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = PlaceholderImg;
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Carousel Indicators */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentSlide(index);
                pauseAutoSlide();
              }}
              className={`w-2 h-2 rounded-full transition ${
                currentSlide === index ? "bg-white w-8" : "bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Free Delivery Banner */}
      {/* <div className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="bg-gray-700 rounded-full p-4">
            <Truck size={32} />
          </div>
          <div className="text-center sm:text-left">
            <h3 className="text-2xl font-bold text-white mb-1">
              Free Delivery
            </h3>
            <p className="text-white">
              Free delivery when you order from a restaurant
            </p>
          </div>
        </div>
      </div> */}

      {error ? (
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-24">
            <div className="text-red-500 mb-6">
              <svg
                className="w-24 h-24 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Oops! Something went wrong
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              We couldn't load the marketplace data. Please check your
              connection and try again.
            </p>
            <button
              onClick={fetchMarketplaceData}
              className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition"
            >
              Try Again
            </button>
          </div>
        </main>
      ) : (
        <main className="max-w-7xl mx-auto px-4 py-8">
          {/* Top Trending Restaurants */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Top Trending Restaurants
              </h2>
              <button
                onClick={() => navigate("/restaurant")}
                className="text-gray-600 font-semibold hover:text-gray-500"
              >
                View All
              </button>
            </div>

            {loading ? (
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-sm min-w-[280px] sm:min-w-[320px] snap-start flex-shrink-0 animate-pulse"
                  >
                    <div className="w-full h-48 bg-gray-200"></div>
                    <div className="p-4">
                      <div className="h-6 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : marketplaceData?.restaurants &&
              marketplaceData.restaurants.length > 0 ? (
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
                {marketplaceData.restaurants
                  .sort((a, b) => b.isOpen - a.isOpen)
                  .map((restaurant, index) => (
                    <BusinessCard
                      key={restaurant.id || index}
                      item={{
                        id: restaurant.id,
                        name: restaurant.name,
                        address: restaurant.address,
                        openTime: restaurant.openTime,
                        image: restaurant.bannerImage,
                        businessId: restaurant.businessId,
                        rating: 4.5,
                        reviews: restaurant.recentOrders || 0,
                        cuisine: "Local Cuisine",
                        type: restaurant.isOpen ? "Open" : "Closed",
                        time: `${restaurant.minReadyTime}-${restaurant.maxReadyTime} min`,
                      }}
                      type="restaurant"
                      isOpen={restaurant.isOpen}
                    />
                  ))}
              </div>
            ) : (
              <EmptyState
                title="No Restaurants Available"
                message="No restaurants found in this location"
              />
            )}
          </div>

          {/* Top Selling Wholesale Items */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Top Selling Wholesale Items
              </h2>
              <button
                onClick={() => navigate("/raw_food")}
                className="text-gray-600 font-semibold hover:text-gray-500"
              >
                View All
              </button>
            </div>

            {marketplaceData?.rawFoods &&
            marketplaceData.rawFoods.length > 0 ? (
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
                {marketplaceData.rawFoods.map((food, index) => (
                  <BusinessCard key={index} item={food} type="raw_food" />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No Wholesale Items Available"
                message="No wholesale items found in this location"
              />
            )}
          </div>

          {/* Top Trending Retail Stores */}
          <div className="mb-36">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Top Trending Retail Stores
              </h2>
              <button
                onClick={() => navigate("/store")}
                className="text-gray-600 font-semibold hover:text-gray-500"
              >
                View All
              </button>
            </div>

            {marketplaceData?.stores && marketplaceData.stores.length > 0 ? (
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
                {marketplaceData.stores.map((store, index) => (
                  <BusinessCard key={index} item={store} type="store" />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No Stores Available"
                message="No stores found in this location"
              />
            )}
          </div>
        </main>
      )}
      <FooterDisplay />

      {/* Floating Cart Button */}
      {cartCount > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-black text-white rounded-full p-4 shadow-lg hover:bg-gray-800 transition z-40"
        >
          <ShoppingCart size={24} />
          <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {cartCount}
          </span>
        </button>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-t-2xl rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Your Cart ({cartCount})</h3>
              <button
                onClick={() => setShowCart(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <p className="text-gray-600 text-center py-8">
              Your cart items will appear here
            </p>
            <button className="w-full bg-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition">
              Checkout
            </button>
          </div>
        </div>
      )}
      <HelpSupportButton page="MARKETPLACE" />
    </div>
  );
}
